import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, MapPin, Clock } from "lucide-react";

function formatEventDate(iso) {
    try {
        const d = new Date(iso + "T00:00:00");
        return {
            day: d.toLocaleDateString("es-ES", { day: "2-digit" }),
            month: d.toLocaleDateString("es-ES", { month: "short" }).replace(".", "").toUpperCase(),
            year: d.toLocaleDateString("es-ES", { year: "numeric" }),
        };
    } catch {
        return { day: "", month: "", year: "" };
    }
}

export default function Events({ events = [] }) {
    if (!events || events.length === 0) return null;
    return (
        <section
            id="eventos"
            data-testid="events-section"
            className="bg-white border-b border-[#E2E4E9]"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
                    <div className="max-w-2xl">
                        <div className="text-xs uppercase tracking-widest text-[#082050]/70 mb-4">
                            Próximos eventos
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-semibold text-[#082050] tracking-tight">
                            Agenda de ASATTA.
                        </h2>
                        <p className="mt-4 text-[#4A5568] text-lg">
                            Encuentros, formaciones y jornadas para los alojamientos
                            asociados y el sector turístico de Tarifa.
                        </p>
                    </div>
                    <Link
                        to="/eventos"
                        data-testid="btn-all-events"
                        className="inline-flex items-center gap-2 text-sm font-medium text-[#082050] hover:opacity-70"
                    >
                        Ver todos los eventos
                        <ArrowRight size={14} />
                    </Link>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {events.map((e, i) => {
                        const d = formatEventDate(e.event_date);
                        return (
                            <article
                                key={e.id || i}
                                data-testid={`event-card-${i}`}
                                className="border border-[#E2E4E9] hover:border-[#082050] transition-colors flex flex-col group"
                            >
                                {e.image && (
                                    <div className="relative aspect-[16/10] overflow-hidden bg-[#F8F9FA]">
                                        <img
                                            src={e.image}
                                            alt={e.title}
                                            loading="lazy"
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                        />
                                        <div className="absolute top-4 left-4 bg-white border border-[#082050]/10 px-4 py-2 text-center">
                                            <div className="text-2xl font-semibold text-[#082050] leading-none">
                                                {d.day}
                                            </div>
                                            <div className="text-[10px] uppercase tracking-widest text-[#082050]/60 mt-1">
                                                {d.month}
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div className="p-6 flex-1 flex flex-col">
                                    <h3 className="text-lg font-semibold text-[#082050] tracking-tight">
                                        {e.title}
                                    </h3>
                                    <p className="mt-3 text-sm text-[#4A5568] leading-relaxed line-clamp-3">
                                        {e.description}
                                    </p>
                                    <div className="mt-5 pt-5 border-t border-[#E2E4E9] space-y-2 text-xs text-[#4A5568]">
                                        <div className="flex items-center gap-2">
                                            <Calendar size={12} className="text-[#082050]" />
                                            {d.day} {d.month} {d.year}
                                        </div>
                                        {e.event_time && (
                                            <div className="flex items-center gap-2">
                                                <Clock size={12} className="text-[#082050]" />
                                                {e.event_time}
                                            </div>
                                        )}
                                        {e.location && (
                                            <div className="flex items-center gap-2">
                                                <MapPin size={12} className="text-[#082050]" />
                                                {e.location}
                                            </div>
                                        )}
                                    </div>
                                    {e.cta_url && e.cta_label && (
                                        <a
                                            href={e.cta_url}
                                            data-testid={`event-cta-${i}`}
                                            className="mt-6 inline-flex items-center gap-2 text-xs font-medium text-[#082050] border border-[#082050] px-4 py-2 hover:bg-[#082050] hover:text-white transition-colors self-start"
                                        >
                                            {e.cta_label}
                                            <ArrowRight size={12} />
                                        </a>
                                    )}
                                </div>
                            </article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
